import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ProductItem } from "../../ProductMFE/Products/types/products";
import { SampleProductItems } from "../../ProductMFE/Products/constants/productItems";

export interface ProductItemState {
  productItemList: ProductItem[];
  /** Pagination metadata from last list response — matches API field names. */
  productItemTotalElements: number;
  productItemTotalPages: number;
  productItemCurrentPage: number;
  productItemPageSize: number;
  productItemDetail?: ProductItem;
  /**
   * COMPANY_ADMIN: selected store for product-item list filter, keyed by companyId (Redux only).
   */
  companyAdminItemListStoreIdByCompany: Record<string, string>;
}

export const initialProductItemState: ProductItemState = {
  productItemList: SampleProductItems,
  productItemTotalElements: 0,
  productItemTotalPages: 0,
  productItemCurrentPage: 0,
  productItemPageSize: 20,
  productItemDetail: undefined,
  companyAdminItemListStoreIdByCompany: {}
};

export const productItemSlice = createSlice({
  name: "productItem",
  initialState: initialProductItemState,
  reducers: {
    setProductItemList: (state, action: PayloadAction<ProductItem[]>) => {
      state.productItemList = action.payload;
      state.productItemTotalElements = 0;
      state.productItemTotalPages = 0;
      state.productItemCurrentPage = 0;
      state.productItemPageSize = 20;
    },
    setProductItemListWithTotal: (
      state,
      action: PayloadAction<{
        items: ProductItem[];
        totalElements: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
      }>
    ) => {
      state.productItemList = action.payload.items;
      state.productItemTotalElements = action.payload.totalElements;
      state.productItemTotalPages = action.payload.totalPages;
      state.productItemCurrentPage = action.payload.currentPage;
      state.productItemPageSize = action.payload.pageSize;
    },
    resetProductItemList: (state) => {
      state.productItemList = initialProductItemState.productItemList;
      state.productItemTotalElements =
        initialProductItemState.productItemTotalElements;
      state.productItemTotalPages =
        initialProductItemState.productItemTotalPages;
      state.productItemCurrentPage =
        initialProductItemState.productItemCurrentPage;
      state.productItemPageSize = initialProductItemState.productItemPageSize;
    },
    setCompanyAdminItemListStoreFilter: (
      state,
      action: PayloadAction<{ companyId: string; storeId: string | null }>
    ) => {
      const companyId = action.payload.companyId?.trim();
      if (!companyId) return;
      const sid = action.payload.storeId?.trim();
      if (!sid) {
        delete state.companyAdminItemListStoreIdByCompany[companyId];
      } else {
        state.companyAdminItemListStoreIdByCompany[companyId] = sid;
      }
    },
    // Add product items from pagination or bulk creation
    addProductItems: (state, action: PayloadAction<ProductItem[]>) => {
      state.productItemList = [...state.productItemList, ...action.payload];
    },
    // Add single product item
    addProductItem: (state, action: PayloadAction<ProductItem>) => {
      state.productItemList = [...state.productItemList, action.payload];
    },
    setProductItemDetail: (state, action: PayloadAction<ProductItem>) => {
      state.productItemDetail = action.payload;
    },
    resetProductItemDetail: (state) => {
      state.productItemDetail = initialProductItemState.productItemDetail;
    },
    // Update existing product item in list and detail if matched
    updateProductItem: (state, action: PayloadAction<ProductItem>) => {
      const index = state.productItemList.findIndex(
        (item) => item.id === action.payload.id
      );
      if (index !== -1) {
        state.productItemList[index] = action.payload;
      }
      if (state.productItemDetail?.id === action.payload.id) {
        state.productItemDetail = action.payload;
      }
    },
    // Remove product item from list and clear detail if matched
    removeProductItem: (state, action: PayloadAction<string>) => {
      state.productItemList = state.productItemList.filter(
        (item) => item.id !== action.payload
      );
      if (state.productItemDetail?.id === action.payload) {
        state.productItemDetail = undefined;
      }
    }
  }
});

export const {
  setProductItemList,
  setProductItemListWithTotal,
  resetProductItemList,
  setCompanyAdminItemListStoreFilter,
  addProductItems,
  addProductItem,
  setProductItemDetail,
  resetProductItemDetail,
  updateProductItem,
  removeProductItem
} = productItemSlice.actions;

export default productItemSlice.reducer;
